import { useId } from 'react'
import { useIntl } from 'react-intl'
import InfoTip from '../../../components/novarium/InfoTip'
import { HospitalizationDailyPointDTO } from '../../../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import { PeakAnalysis } from '../../ServicesStatistics/peaks'
import { dayChipOf } from '../hospitalizationEvolutionModel'
import { formatCount, formatPercent } from '../hospitalizationSummaryFormat'

interface HospitalizationPeakChipsProps {
  days: HospitalizationDailyPointDTO[]
  analysis: PeakAnalysis
  /** Abre el panel del día; `opener` es el chip, a donde vuelve el foco al cerrar el panel. */
  onOpen: (day: HospitalizationDailyPointDTO, opener: HTMLElement) => void
}

/**
 * «Picos»: un botón por cada pico de ocupación marcado en el gráfico («Sábado 19/09 · 103,0 % · 202 de 196 camas ›»), la
 * entrada más fácil con el dedo y con lector de pantalla. Abre el mismo panel que el click en el gráfico. Sin picos no se
 * muestra nada. El ⓘ dice el criterio, con la cantidad mínima de camas que usó el cálculo.
 */
export default function HospitalizationPeakChips({ days, analysis, onOpen }: HospitalizationPeakChipsProps) {
  const intl = useIntl()
  const labelId = useId()
  if (analysis.peaks.length === 0) return null

  return (
    <div className="nv-hosp-peaks">
      <span className="nv-hosp-peaks__label">
        <span className="nv-strong" id={labelId}>{intl.formatMessage({ id: 'HospitalizationSummaryPeaksTitle' })}</span>
        <InfoTip text={intl.formatMessage({ id: 'HospitalizationSummaryPeaksCriteria' }, { min: formatCount(intl, analysis.minAttentions) })} />
      </span>
      <ul className="nv-hosp-peaks__list" aria-labelledby={labelId}>
        {analysis.peaks.map((index) => {
          const day = days[index]
          return (
            <li key={day.date}>
              <button type="button" className="nv-hosp-peak-chip" onClick={(event) => onOpen(day, event.currentTarget)}>
                <span>
                  {intl.formatMessage({ id: 'HospitalizationSummaryPeaksChip' }, {
                    period: dayChipOf(intl, day.date),
                    value: formatPercent(intl, day.occupancyPct),
                    beds: intl.formatMessage({ id: 'HospitalizationSummaryOfBeds' }, {
                      count: formatCount(intl, day.patientDays), total: formatCount(intl, day.bedsAvailable),
                    }),
                  })}
                </span>
                <i className="pi pi-angle-right" aria-hidden="true" />
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
